'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { tagListMachine } from './machines/tagListMachine'
import type { CrmTag, CrmHierarchicalTag } from './types'
import {
  buildTagHierarchy,
  getAllDescendants,
  canSetParent as canSetParentUtil,
  flattenHierarchicalTags,
} from './tag-tree'

export type TagListMachineApi = ReturnType<typeof useMachine<typeof tagListMachine>>

const TagListMachineContext = createContext<TagListMachineApi | null>(null)

export function TagListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(tagListMachine)
  return <TagListMachineContext.Provider value={api}>{children}</TagListMachineContext.Provider>
}

export function useTagListMachine() {
  const ctx = useContext(TagListMachineContext)
  if (!ctx) {
    throw new Error('useTagListMachine must be used within TagListMachineProvider')
  }
  return ctx
}

export function useTagListMachineOptional() {
  return useContext(TagListMachineContext)
}

export function useTagActions() {
  const machineApi = useTagListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setTags: (tags: CrmTag[]) => send({ type: 'SET_TAGS', tags }),
    addTag: (tag: CrmTag) => send({ type: 'ADD_TAG', tag }),
    updateTag: (id: string, updates: Partial<CrmTag>) =>
      send({ type: 'UPDATE_TAG', id, updates }),
    removeTag: (id: string) => send({ type: 'REMOVE_TAG', id }),
    removeCascade: (id: string) => send({ type: 'REMOVE_CASCADE', id }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useTagListStoreCompat() {
  const machineApi = useTagListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const tags = state.context.tags
    const isLoading = state.context.isLoading

    const getTagById = (id: string) => tags.find((t) => t.id === id)
    const getTagsByParent = (parentId: string | null) =>
      tags.filter((t) => (t.parentId ?? null) === parentId)
    const getHierarchicalTags = () => buildTagHierarchy(tags)
    const getRootTags = () => buildTagHierarchy(tags)
    const getChildrenTags = (parentId: string) => {
      const hierarchical = buildTagHierarchy(tags)
      const flatTags = flattenHierarchicalTags(hierarchical)
      const parent = flatTags.find((t) => t.id === parentId)
      return (parent?.children || []) as CrmHierarchicalTag[]
    }
    const getTagPath = (tagId: string) => {
      const hierarchical = buildTagHierarchy(tags)
      const flatTags = flattenHierarchicalTags(hierarchical)
      const tag = flatTags.find((t) => t.id === tagId)
      return tag?.fullPath || ''
    }
    const searchTags = (query: string) => {
      const lowerQuery = query.toLowerCase()
      return tags.filter(
        (t) =>
          t.name.toLowerCase().includes(lowerQuery) ||
          t.description?.toLowerCase().includes(lowerQuery)
      )
    }
    const canSetParent = (childId: string, newParentId: string | null) =>
      canSetParentUtil(tags, childId, newParentId)

    return {
      tags,
      isLoading,
      setTags: (t: CrmTag[]) => send({ type: 'SET_TAGS', tags: t }),
      addTag: (tag: CrmTag) => send({ type: 'ADD_TAG', tag }),
      updateTag: (id: string, updates: Partial<CrmTag>) =>
        send({ type: 'UPDATE_TAG', id, updates }),
      removeTag: (id: string) => send({ type: 'REMOVE_TAG', id }),
      removeCascade: (id: string) => send({ type: 'REMOVE_CASCADE', id }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      getTagById,
      getTagsByParent,
      getHierarchicalTags,
      getRootTags,
      getChildrenTags,
      getTagPath,
      searchTags,
      canSetParent,
      initializeDefaults: () => {
        console.log('[TagListMachine] initializeDefaults - tags loaded by service')
      },
      resetToDefaults: (_strategy: 'merge' | 'replace') => {
        console.log('[TagListMachine] resetToDefaults - tags loaded by service')
      },
    }
  }, [machineApi])
}
